import 'aos/dist/aos.css';
import 'i18n';
import 'moment/locale/de';
import 'nprogress/nprogress.css';
import '~/scss/App.scss';
import '~/styles/tailwind.css';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faEnvelope, faPhoneAlt } from '@fortawesome/free-solid-svg-icons';
import useModalManager from '@hooks/useModalManager';
import { Analytics } from '@vercel/analytics/react';
import AOS from 'aos';
import axios from 'axios';
import { Chart } from 'chart.js';
import { CookieWrapper } from 'components/CookieWrapper';
import useToasts from 'components/ToastManager/useToasts';
import i18n from 'i18n';
import Cookies from 'js-cookie';
import { isArray, omit } from 'lodash';
import moment from 'moment';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import NProgress from 'nprogress';
import { useEffect } from 'react';
import { I18nProvider } from 'react-aria-components';
import { Provider } from 'react-redux';
import { pushAlert } from 'redux/actions/alertActions';
import { loadUser } from 'redux/actions/authActions';
import store from 'redux/store';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { SWRConfig } from 'swr';
import theme from 'theme';
import ClubContext from '~/components/ClubContext';
import GlobalOverlay from '~/components/GlobalOverlay';
import { getActiveToken } from '~/helpers/authToken';
import { swrRetryPolicy, throwIfFailedRead } from '~/lib/apiErrors';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import FloatingPortal from '../components/FloatingPortal';
import ModalManager from '../components/ModalManager';
import ToastManager from '../components/ToastManager/ToastManager';
import CommandPalette from '../components/CommandPalette/CommandPalette';

axios.defaults.withCredentials = true;
library.add(faEnvelope, faPhoneAlt);

// set default chart options
Chart.defaults.font.family = 'Nunito';

const NProgressStyles = createGlobalStyle`
    #nprogress .bar {
        background: ${(props) => props.theme.colors.green};
    }

    #nprogress .spinner-icon {
        border-top-color: ${(props) => props.theme.colors.green};
        border-left-color: ${(props) => props.theme.colors.green};
    }
`;

Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());

// Global SWR fetcher: builds the API URL from the key, attaches auth headers and toasts unexpected errors
export const fetcher = (key: string | [string, object], init: RequestInit, throwErrors = false) => {
    let resource = key as string,
        params = {};
    if (isArray(key)) {
        resource = key?.[0];
        params = key?.[1];
    }

    const url = new URL(
        resource.startsWith('http') || resource.startsWith('api/')
            ? resource
            : process.env.NEXT_PUBLIC_API_HOST + resource
    );

    const stringify = (value: any) => {
        switch (typeof value) {
            case 'object':
                return JSON.stringify(value);
            default:
                return String(value);
        }
    };

    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((v, i) => url.searchParams.append(`${key}[${i}]`, stringify(v)));
        } else url.searchParams.append(key, stringify(value));
    });

    // Send the bearer header only when a token actually exists. The previous unconditional
    // interpolation produced the literal string "Bearer undefined" for every guest request.
    const token = getActiveToken();

    return fetch(url.toString(), {
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ['Content-Type']: 'application/json',
            ['X-Requested-With']: 'XMLHttpRequest',
            ['Content-Language']: Router.locale || 'de'
        },
        ...init
    }).then(async (res) => {
        if (res.status === 503) {
            Router.replace('/maintenance');
            return { data: [] };
        }

        // 423 = club locked over open chargebacks — full-page lock, nothing to render inline
        if (res.status === 423) {
            if (Router.pathname !== '/gesperrt') Router.replace('/gesperrt');
            return { data: [] };
        }

        const data = await res.json();
        // Only toast a 403 when it isn't a missing-permission denial or a missing subscription
        // (both are handled inline: CheckSubscription renders the upgrade card on gated pages,
        // while secondary tools just get empty data), and it isn't the permission-probe endpoint.
        const isPermissionDenied = data?.error === 'App\\Exceptions\\Club\\PermissionDenied';
        const isSubscriptionRequired = data?.error === 'SubscriptionRequired';
        if (
            res.status === 403 &&
            !isPermissionDenied &&
            !isSubscriptionRequired &&
            !resource.match(/clubs\/(.*)\/check/gm)
        )
            store.dispatch(pushAlert(data?.message, 'danger'));
        if (throwErrors && res.status === 500) throw new Error(data);

        // A failed read has to fail — see lib/apiErrors, which app/layout.tsx
        // reads the same rule from so both routers answer a failure alike.
        throwIfFailedRead(res, data);

        return data;
    });
};

/**
 * When each failing key was last announced, so SWR's own retries do not stack
 * a toast per attempt on top of each other. Module-level on purpose: the app
 * component re-renders, the memory of what was already said must not.
 */
const swrErrorToastedAt = new Map<string, number>();
const SWR_ERROR_TOAST_INTERVAL = 30_000;

/**
 * Drops the keys whose silence has run out.
 *
 * An expired entry and a missing one mean the same thing to the check below, so the
 * map only ever has to remember the last half minute. Without this it remembers every
 * key that has ever failed, for as long as the tab is open.
 */
const forgetExpiredErrorToasts = (now: number) => {
    swrErrorToastedAt.forEach((at, key) => {
        if (now - at >= SWR_ERROR_TOAST_INTERVAL) swrErrorToastedAt.delete(key);
    });
};

const MyApp = ({ Component, pageProps }: AppProps) => {
    const { locale, query, replace, pathname, events } = useRouter();
    const addToast = useToasts((state) => state.addToast);
    const resetClosableModals = useModalManager((state) => state.resetClosableModals);
    NProgress.configure({ showSpinner: false });

    // /iframe/* pages render inside a third-party website. They get a stripped shell: no
    // verification banner, no command palette, no analytics and no scroll animations — none of
    // that belongs in an embedded widget, and all of it costs payload the host page pays for.
    const isEmbed = pathname.startsWith('/iframe');

    // on first load
    useEffect(() => {
        // login user from cookies
        if (!store.getState().auth.isAuthenticated && Cookies.get('token')) {
            store.dispatch(loadUser());
        }
    }, []);

    // reset modals on back/forward navigation — except the ones marked preventClose, which
    // block on purpose (the form migration gate) and would otherwise be escapable by
    // pressing the browser's back button.
    useEffect(() => {
        const handler = () => {
            resetClosableModals();
        };
        // Shallow changes rewrite the URL without a real page transition —
        // `PriceListPage`'s own template picker is exactly this, pushing
        // `?template=` so a template stays deep-linkable. Resetting on those
        // too closed any modal open above it the instant a template was
        // picked, which is also why that screen couldn't be embedded in the
        // plan setup wizard until now.
        const handleRouteChangeComplete = (_url: string, { shallow }: { shallow?: boolean } = {}) => {
            // `resetClosableModals`, not `resetModals`: a real navigation is
            // exactly the case the gate has to survive, and the store says so
            // — navigation closes only what the user could have closed anyway.
            if (!shallow) resetClosableModals();
        };

        window.addEventListener('popstate', handler);
        events.on('routeChangeComplete', handleRouteChangeComplete);

        return () => {
            window.removeEventListener('popstate', handler);
            events.off('routeChangeComplete', handleRouteChangeComplete);
        };
    }, []);

    // change language on locale change
    useEffect(() => {
        i18n.changeLanguage(locale);
        moment.locale(locale);
    }, [locale]);

    // on every load
    useEffect(() => {
        if (isEmbed) return;

        AOS.init({
            once: true,
            duration: 800,
            offset: 200,
            disable: 'phone'
        });
    }, [Component, isEmbed]);

    useEffect(() => {
        if (!!query.notify) {
            try {
                const data = JSON.parse(decodeURIComponent(query.notify as string));

                addToast({
                    type: 'info',
                    title: data.title as string,
                    description: data.description as string
                });

                replace(
                    {
                        pathname,
                        query: omit(query, 'notify')
                    },
                    undefined,
                    { shallow: true }
                );
            } catch (error) {
                console.error('Failed to parse notification:', error);
            }
        }
    }, [query]);

    return (
        <>
            <ThemeProvider theme={theme}>
                <SWRConfig
                    value={{
                        fetcher,
                        ...swrRetryPolicy,
                        // Screens with their own error state keep it; this is
                        // the floor under everything that has none, so a failed
                        // read is at least said out loud instead of rendering
                        // as an empty state.
                        onError: (error, key) => {
                            const status = (error as { status?: number })?.status;
                            // Network hiccups retry silently; server answers
                            // are worth a sentence.
                            if (!status) return;

                            const now = Date.now();
                            forgetExpiredErrorToasts(now);

                            const last = swrErrorToastedAt.get(key) ?? 0;
                            if (now - last < SWR_ERROR_TOAST_INTERVAL) return;
                            swrErrorToastedAt.set(key, now);

                            addToast({
                                type: 'error',
                                title: i18n.t('common:loadErrorTitle'),
                                description:
                                    (error as { info?: { message?: string } })?.info?.message ||
                                    i18n.t('common:loadErrorDescription')
                            });
                        }
                    }}
                >
                    <Provider store={store}>
                        <I18nProvider locale={locale || 'de'}>
                            <Head>
                                <title>Digitalisiere deine Tennisschule auf Sportision</title>
                                <link rel="apple-touch-icon" href="/logo192.png" />
                                <link rel="shortcut icon" type="image/x-icon" href="/favicon.ico" />
                                <meta name="google" content="notranslate" />
                                <meta name="apple-itunes-app" content="app-id=6476008864" />
                                <meta
                                    name="description"
                                    content="Nutze unsere Möglichkeiten zur Online Buchung deiner Trainingsangebote im Tennis, sowie um deinen Trainingsplan automatisch zu erstellen."
                                />
                                <meta name="keywords" content="Trainingsplan, Online Anmeldungen" />
                                <meta name="viewport" content="width=device-width, initial-scale=1" key="viewport" />
                            </Head>
                            <NProgressStyles />
                            <CookieWrapper>
                                <ClubContext.Provider
                                    value={Array.isArray(query.slug) ? query.slug[0] : (query.slug as string)}
                                >
                                    {!isEmbed && <EmailVerificationBanner />}
                                    <Component {...pageProps} />
                                    <ToastManager />
                                    <ModalManager />
                                    {!isEmbed && <CommandPalette />}
                                    <GlobalOverlay />
                                    <FloatingPortal />
                                </ClubContext.Provider>
                            </CookieWrapper>
                        </I18nProvider>
                    </Provider>
                </SWRConfig>
            </ThemeProvider>
            {!isEmbed && <Analytics />}
        </>
    );
};

export default MyApp;
